import React from 'react';
import PropTypes from 'prop-types';

import { StructureOptionType } from '@Pages/RetroSynthesis/StructureOption/constants';
import SvgUseAsButton from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteTreeView/components/SvgUseAsButton';

// RX-3549 - remove imports once feature switcher is removed
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getShoppingCartIcon } from '@Components/Results/structure/availability/ShoppingCartHelper';
import ReactDOMServer from 'react-dom/server';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import { isCustomerIconVisible, getCustomerSpecificMapping } from '@Components/Results/structure/structure-utils';
import StructureToolbarCustomerIconTooltip from '@Components/Results/structure/StructureToolbarCustomerIconTooltip';
import { Badge } from '@els/els-react--badge';
import NumberFormatter from '@Utils/NumberFormatter';

// Button positioning constants
const BUTTON_Y_OFFSET = 42;
const CUSTOMER_BUTTON_X_OFFSET = 5;
const CUSTOMER_BUTTON_Y_OFFSET = 43.5;
const SHOPPING_CART_X_BASE = 35;
const ZOOM_BUTTON_X_BASE = 70;
const DETAILS_BUTTON_X_BASE = 103;
const OPTIONS_BUTTON_X_BASE = 137;
const SUPPLIER_BADGE_X_BASE = 20;
const SUPPLIER_BADGE_Y_OFFSET = 29;

// Button size constants
const CUSTOMER_BUTTON_SIZE = 32;
const STANDARD_BUTTON_SIZE = 26;

const customerButton = (x, substanceHeight, icon, label) => ({
  href: `#icon-${icon}`,
  ariaLabel: label,
  height: CUSTOMER_BUTTON_SIZE,
  width: CUSTOMER_BUTTON_SIZE,
  x: x - CUSTOMER_BUTTON_X_OFFSET,
  y: substanceHeight - CUSTOMER_BUTTON_Y_OFFSET,
});

const shoppingCartButton = (offsetX, substanceHeight, icon, title) => ({
  sprite: icon,
  useButton: true,
  ariaLabel: title,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x: SHOPPING_CART_X_BASE + offsetX,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const zoomButton = (offsetX, substanceHeight) => ({
  sprite: 'ZoomIn',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x: ZOOM_BUTTON_X_BASE + offsetX,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const detailsButton = (offsetX, substanceHeight) => ({
  sprite: 'TextFile',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x: DETAILS_BUTTON_X_BASE + offsetX,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

const synthesisPlannerButton = (offsetX, substanceHeight) => ({
  sprite: 'Ellipsis',
  useButton: true,
  height: STANDARD_BUTTON_SIZE,
  width: STANDARD_BUTTON_SIZE,
  x: OPTIONS_BUTTON_X_BASE + offsetX,
  y: substanceHeight - BUTTON_Y_OFFSET,
});

function StructureButtons({
  substanceHeight,
  shoppingCartVisible,
  onOptionClick,
  isZoomAvailable,
  supplierNumber,
  viewDetailsEnabled,
  optionsEnabled,
  substanceDetails,
  customerInfo,
}) {
  const showCustomerIcon = isCustomerIconVisible(customerInfo);
  const offsetX = showCustomerIcon && shoppingCartVisible ? 20 : 0;
  const ciX = shoppingCartVisible ? 10 : 40;
  const shoppingCartTooltipLabel = 'Number of Suppliers';

  const { icon: customerIcon, label } = getCustomerSpecificMapping(customerInfo.name) || {};
  const shoppingCartIcon = getShoppingCartIcon(substanceDetails);
  const shoppingCartTitle = shoppingCartIcon === 'RapidDelivery' ? 'Rapid Substance availability' : 'Substance availability';
  const customerIconAriaLabel = `Copy SMILES from ${label}`;

  return (
    <>
      {showCustomerIcon && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.CUSTOMER_ICON,
              targetRect: e?.target?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className={`rt-project-route-substance-node--customer-icon ${customerIcon}`}
          tabIndex="0"
          tooltip={ReactDOMServer.renderToString(<StructureToolbarCustomerIconTooltip customerLabel={label} />)}
          {...customerButton(ciX, substanceHeight, customerIcon, customerIconAriaLabel)}
        />
      )}

      {shoppingCartVisible && (
        <>
          <SvgUseAsButton
            onClick={(e) =>
              onOptionClick({
                type: StructureOptionType.SHOPPING_CARD,
                targetRect: e?.target?.getBoundingClientRect(),
              })
            }
            tabIndex="0"
            tooltip={ReactDOMServer.renderToString(
              <ShoppingCartTooltipContent details={substanceDetails} supplierNumber={supplierNumber} />
            )}
            wrapperClassName="rt-project-route-substance-node--button"
            className="rt-project-route-substance-node--purchase-button"
            {...shoppingCartButton(offsetX, substanceHeight, shoppingCartIcon, shoppingCartTitle)}
          />
          {Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && !!supplierNumber && (
            <foreignObject x={SUPPLIER_BADGE_X_BASE + offsetX} y={substanceHeight - SUPPLIER_BADGE_Y_OFFSET} width={26} height={20}>
              <Badge
                type="subtle"
                size="base"
                label={shoppingCartTooltipLabel}
                title={shoppingCartTooltipLabel}
                content={NumberFormatter(supplierNumber)}
                overlap="space-1o2"
              />
            </foreignObject>
          )}
        </>
      )}
      {isZoomAvailable && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.ZOOM,
              targetRect: e?.target?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="Zoom in on structure"
          {...zoomButton(offsetX, substanceHeight)}
        />
      )}
      {viewDetailsEnabled && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.DETAILS,
              targetRect: e?.target?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--zoom"
          tabIndex="0"
          tooltip="Show Details"
          {...detailsButton(offsetX, substanceHeight)}
        />
      )}
      {optionsEnabled && (
        <SvgUseAsButton
          onClick={(e) =>
            onOptionClick({
              type: StructureOptionType.OPTIONS,
              targetRect: e?.target?.getBoundingClientRect(),
            })
          }
          wrapperClassName="rt-project-route-substance-node--button"
          className="rt-project-route-substance-node--synthesis-planner"
          tabIndex="0"
          tooltip="Show Options"
          {...synthesisPlannerButton(offsetX, substanceHeight)}
        />
      )}
    </>
  );
}

StructureButtons.defaultProps = {
  onOptionClick: () => {},
  isZoomAvailable: true,
  customerInfo: {},
};

StructureButtons.propTypes = {
  substanceHeight: PropTypes.number,
  supplierNumber: PropTypes.number,
  shoppingCartVisible: PropTypes.bool,
  onOptionClick: PropTypes.func,
  isZoomAvailable: PropTypes.bool,
  optionsEnabled: PropTypes.bool,
  viewDetailsEnabled: PropTypes.bool,
  substanceDetails: PropTypes.object,
  customerInfo: PropTypes.object,
};

export default StructureButtons;
